import React, { useState, useRef } from 'react';
import { User } from '../../models/User';
import { PhotographIcon, XIcon, VideoCameraIcon } from '@heroicons/react/outline';
import { motion, AnimatePresence } from 'framer-motion';

interface PostCreatorProps {
  user: User;
  onPostCreated: (content: string, files: File[]) => Promise<void>;
}

export const PostCreator: React.FC<PostCreatorProps> = React.memo(({ user, onPostCreated }) => {
  const [content, setContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasVideo = selectedFiles.some(f => f.type.startsWith('video/'));

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    if (selectedFiles.length + files.length > 5) {
      setError('Maximum 5 files allowed per post.');
      return;
    }

    const newFiles = [...selectedFiles];
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      
      if (isVideo) {
        if (newFiles.some(f => f.type.startsWith('video/'))) {
          setError('Only one video is allowed per post.');
          continue;
        }
        if (file.size > 20 * 1024 * 1024) {
          setError('Videos must be under 20MB.');
          continue;
        }
      }

      newFiles.push(file);
    }

    setSelectedFiles(newFiles);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!content.trim() && selectedFiles.length === 0) return;
    if (content.length > 1000) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onPostCreated(content, selectedFiles);
      setContent('');
      setSelectedFiles([]);
    } catch (err: any) {
      setError(err.message || 'Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4">
        <div className="h-10 w-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center shrink-0">
          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
        </div>
        <div className="flex-grow min-w-0 flex flex-col gap-2">
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-3 sm:p-4 ring-1 ring-slate-200/50 dark:ring-white/5 focus-within:ring-blue-500/50 transition-[background-color,ring]">
            <textarea
              placeholder="Share something with the family..."
              className="w-full bg-transparent border-none focus:ring-0 text-base sm:text-lg resize-none dark:text-white p-0"
              rows={3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              maxLength={1000}
            />
            
            <div className="flex justify-end mt-2">
              <span className={`text-[9px] font-black tracking-widest uppercase ${
                content.length >= 900 ? 'text-red-500' : 'text-slate-400 dark:text-slate-500'
              }`}>
                {content.length} / 1000
              </span>
            </div>
          </div>
          
          <AnimatePresence>
            {selectedFiles.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="grid grid-cols-5 gap-2 mt-4"
              >
                {selectedFiles.map((file, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 border dark:border-slate-600 group">
                    {file.type.startsWith('video/') ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-slate-800 text-white">
                        <VideoCameraIcon className="h-6 w-6 text-blue-400" />
                        <span className="text-[8px] font-bold mt-1 uppercase truncate px-1 w-full text-center">{file.name}</span>
                      </div>
                    ) : (
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                    )}
                    <button
                      onClick={() => removeFile(i)}
                      className="absolute top-1 right-1 p-1 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {error && (
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-500 text-sm font-medium mt-2"
            >
              {error}
            </motion.p>
          )}

          <div className="mt-6 pt-4 border-t dark:border-slate-700 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={selectedFiles.length >= 5}
                className="p-2 sm:p-2.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-xl transition-[background-color,transform] flex items-center gap-2 disabled:opacity-50"
                title="Add Photos or Video"
              >
                <div className="flex -space-x-1">
                  <PhotographIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  {!hasVideo && <VideoCameraIcon className="h-5 w-5 sm:h-6 sm:w-6" />}
                </div>
                <span className="text-xs sm:text-sm font-bold">Add Media</span>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*,video/mp4,video/quicktime"
                multiple
                onChange={handleFileChange}
              />
              <span className="text-[10px] sm:text-xs text-slate-400 font-medium ml-1">
                {selectedFiles.length}/5
              </span>
            </div>

            <button
              onClick={handleSubmit}
              disabled={isSubmitting || (!content.trim() && selectedFiles.length === 0) || content.length > 1000}
              className="flex-grow sm:flex-grow-0 px-6 sm:px-8 py-2 sm:py-2.5 bg-blue-600 text-white rounded-xl font-bold shadow-md hover:bg-blue-700 transition-[background-color,transform,shadow] disabled:opacity-50 disabled:grayscale"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Posting...
                </div>
              ) : 'Post'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});